/**
 * QCE redux slice (Themes data layer): RTK core thunks over qceService. Not RTK Query —
 * its hooks need RTK v2 with react-redux v9 (we're on RTK v1.9), so they can't subscribe.
 */
import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AnyAction, PayloadAction, ThunkDispatch } from "@reduxjs/toolkit";

import type { CodeSelectionDetail } from "../../../Pages/Hub/coding/codeSelectionEvent";
import type { RootState } from "../rootReducer";
import { notifyError, notifyInfo, notifySuccess } from "../../actions/notify";
import type {
  Theme,
  ThemeWrite,
  ThemeCode,
  ClipAttach,
  Exemplar,
  ThemeDraft,
  HighlightCreate,
  AutoDetectConfig,
  AutoDetectRunRequest,
  AutoDetectRunResult,
  ReviewStatus,
} from "../../../Pages/Project/Tabs/ThemesTab/types";
import { appendSuggestions } from "../../../Pages/Project/Tabs/ThemesTab/themeSuggestionsStore";

import { agentService } from "./agentService";
import { qceErrMsg, qceService } from "./qceService";
import type { CodePatch } from "./qceService";

type Status = "idle" | "loading" | "succeeded" | "failed";

// Stable empty reference so selectors don't return a fresh [] each render.
const EMPTY_CODES: ThemeCode[] = [];

export type PendingSelectionAction = "add-to-theme" | "highlight";

export interface PendingSelection {
  detail: CodeSelectionDetail;
  action: PendingSelectionAction;
}

/** An auto-detect run, owned at app level so closing its dialog never cancels it. */
export interface AutoDetectRunState {
  status: "running" | "done" | "error";
  themeIds: string[];
  result?: AutoDetectRunResult;
  error?: string;
}

/** A generate-themes run per suggestions scope (user + project + recordings). */
export interface GenerateRunState {
  status: "running" | "done" | "error";
  /** New suggestions this run appended (0 = nothing new). */
  added?: number;
  error?: string;
}

export interface QceState {
  // Themes list keyed by scope (projectId, or "__global__" for the org-wide tab)
  // so two consumers with different scopes never contend over a single slot (the
  // Auto-detect memory-leak fix). Each scope fetches once and reads its own data.
  themesByScope: Record<string, Theme[]>;
  themesStatusByScope: Record<string, Status>;
  codesByTheme: Record<string, ThemeCode[]>;
  codesStatus: Record<string, Status>;
  storyCodes: Record<string, ThemeCode[]>;
  pendingSelection: PendingSelection | null;
  /** Latest auto-detect run per project (one in flight at a time). */
  autoDetectRuns: Record<string, AutoDetectRunState>;
  /** Latest generate run per suggestions storage key. */
  generateRuns: Record<string, GenerateRunState>;
  /** Hub "Show highlights" overlay toggle — shared so creating a highlight can auto-enable it. */
  showHighlights: boolean;
  // Staleness guards: the requestId of the most recently *dispatched* read for
  // each key. Mutations (add/delete/move) fire several overlapping refetches;
  // under network latency an earlier (pre-mutation) response can resolve last
  // and clobber the store with stale data. We apply a fulfilled read only when
  // it is still the newest in flight, so the final state always reflects the
  // last dispatched fetch. (Works locally where responses resolve in order;
  // manifested on staging as "moved highlight not removed / duplicated".)
  themesReqIdByScope: Record<string, string>;
  codesReqId: Record<string, string>;
  storyCodesReqId: Record<string, string>;
}

const initialState: QceState = {
  themesByScope: {},
  themesStatusByScope: {},
  codesByTheme: {},
  codesStatus: {},
  storyCodes: {},
  pendingSelection: null,
  autoDetectRuns: {},
  generateRuns: {},
  showHighlights: false,
  themesReqIdByScope: {},
  codesReqId: {},
  storyCodesReqId: {},
};

const tokenOf = (getState: () => unknown) => (getState() as RootState).auth?.token;
// Per-scope themes cache key: a projectId, or "__global__" for the org-wide (null) scope.
const GLOBAL_SCOPE = "__global__";
const scopeKey = (projectId: string | null | undefined) => projectId ?? GLOBAL_SCOPE;
const EMPTY_THEMES: Theme[] = [];
const loadedScopeKeys = (getState: () => unknown) => Object.keys((getState() as RootState).qce.themesStatusByScope);
// The project scope currently loaded (non-global) — used to assign a new theme's projectId.
const activeProjectScope = (getState: () => unknown): string | undefined =>
  loadedScopeKeys(getState).find((k) => k !== GLOBAL_SCOPE);
// Refetch every currently-loaded scope so counts stay fresh across the project tab
// and any open global/modal view (each scope is independent).
const refetchLoadedScopes = (getState: () => unknown, dispatch: ThunkDispatch<unknown, unknown, AnyAction>) => {
  for (const k of loadedScopeKeys(getState)) dispatch(fetchThemes(k === GLOBAL_SCOPE ? undefined : k));
};

// ----- Reads -----
// `projectId` scopes the list to one project (project tab); omit for org-wide (global tab).
export const fetchThemes = createAsyncThunk("qce/fetchThemes", (projectId: string | undefined, { getState }) =>
  qceService.listThemes(tokenOf(getState), projectId)
);

export const fetchThemeCodes = createAsyncThunk("qce/fetchThemeCodes", (themeId: string, { getState }) =>
  qceService.listThemeCodes(tokenOf(getState), themeId)
);

export const fetchStoryCodes = createAsyncThunk("qce/fetchStoryCodes", (storyId: string, { getState }) =>
  qceService.listStoryCodes(tokenOf(getState), storyId)
);

// ----- Theme mutations (refetch themes for fresh counts) -----
export const createTheme = createAsyncThunk("qce/createTheme", async (body: ThemeWrite, { getState, dispatch }) => {
  // Assign the current project scope unless the caller set one explicitly.
  const scope = activeProjectScope(getState);
  const payload = scope && body.projectId === undefined ? { ...body, projectId: scope } : body;
  const t = await qceService.createTheme(tokenOf(getState), payload);
  refetchLoadedScopes(getState, dispatch);
  return t;
});

export const updateTheme = createAsyncThunk(
  "qce/updateTheme",
  async ({ id, body }: { id: string; body: ThemeWrite }, { getState, dispatch }) => {
    const t = await qceService.updateTheme(tokenOf(getState), id, body);
    refetchLoadedScopes(getState, dispatch);
    return t;
  }
);

export const deleteTheme = createAsyncThunk("qce/deleteTheme", async (id: string, { getState, dispatch }) => {
  const r = await qceService.deleteTheme(tokenOf(getState), id);
  refetchLoadedScopes(getState, dispatch);
  return r;
});

export const generateThemeFromExamples = createAsyncThunk(
  "qce/generateTheme",
  (exemplars: Exemplar[], { getState }): Promise<ThemeDraft> =>
    qceService.generateThemeFromExamples(tokenOf(getState), exemplars)
);

export const attachClips = createAsyncThunk(
  "qce/attachClips",
  async ({ themeId, clips }: { themeId: string; clips: ClipAttach[] }, { getState, dispatch }) => {
    const r = await qceService.attachClips(tokenOf(getState), themeId, clips);
    refetchLoadedScopes(getState, dispatch);
    dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

// ----- Code mutations -----
// The PATCH returns the full updated code (re-anchored quote / snapped times on a
// boundary edit); the fulfilled reducer swaps it into the caches — no refetch.
// themeId/storyId are accepted for callers' context only; they don't trigger refetches.
export const updateCode = createAsyncThunk(
  "qce/updateCode",
  ({ codeId, patch }: { codeId: string; patch: CodePatch; themeId?: string; storyId?: string }, { getState }) =>
    qceService.updateCode(tokenOf(getState), codeId, patch)
);

// Accept / undo-accept an AI-added excerpt. Optimistic: `pending` applies the
// review to the caches, `rejected` restores `previous` (the caller toasts).
type CodeReview = Pick<ThemeCode, "review_status" | "review_remark" | "reviewed_at">;

export const reviewCode = createAsyncThunk(
  "qce/reviewCode",
  (
    {
      codeId,
      review,
    }: { codeId: string; review: { review_status: ReviewStatus; review_remark?: string }; previous: CodeReview },
    { getState }
  ) => qceService.updateCode(tokenOf(getState), codeId, review)
);

export const deleteCode = createAsyncThunk(
  "qce/deleteCode",
  async ({ codeId, themeId }: { codeId: string; themeId?: string }, { getState, dispatch }) => {
    const r = await qceService.deleteCode(tokenOf(getState), codeId);
    refetchLoadedScopes(getState, dispatch);
    if (themeId) dispatch(fetchThemeCodes(themeId));
    return r;
  }
);

// "Not a fit — teach the theme": record the excerpt (+ optional reason) as a
// theme exclusion, then remove it from the theme with the normal delete flow.
export const rejectCodeWithFeedback = createAsyncThunk(
  "qce/rejectCodeWithFeedback",
  async (
    { themeId, codeId, quote, rationale }: { themeId: string; codeId: string; quote: string; rationale?: string },
    { getState, dispatch }
  ) => {
    await qceService.themeFeedback(tokenOf(getState), themeId, { quote, rationale });
    return dispatch(deleteCode({ codeId, themeId })).unwrap();
  }
);

// Teach the theme from a suggestion the user rejected before it was ever saved.
export const sendThemeFeedback = createAsyncThunk(
  "qce/sendThemeFeedback",
  async ({ themeId, quote, rationale }: { themeId: string; quote: string; rationale?: string }, { getState }) =>
    qceService.themeFeedback(tokenOf(getState), themeId, { quote, rationale })
);

// KS writes AI notes a few seconds after excerpts land — refetch those
// themes' codes once, shortly after, so the notes show without a reload.
const AI_NOTE_DELAY_MS = 5000;
export const refetchCodesForAiNotes =
  (themeIds: string[]) => (dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: () => RootState) => {
    window.setTimeout(() => {
      const loaded = getState().qce.codesStatus;
      themeIds.filter((id) => loaded[id]).forEach((id) => dispatch(fetchThemeCodes(id)));
    }, AI_NOTE_DELAY_MS);
  };

export const attachHighlight = createAsyncThunk(
  "qce/attachHighlight",
  async (body: HighlightCreate, { getState, dispatch }) => {
    const r = await qceService.attachHighlight(tokenOf(getState), body);
    refetchLoadedScopes(getState, dispatch);
    // Refresh the story's codes so the Hub overlay paints the new highlight
    // immediately, without waiting for a manual toggle/refetch.
    dispatch(fetchStoryCodes(body.story_id));
    return r;
  }
);

export const updateExcerptNote = createAsyncThunk(
  "qce/updateExcerptNote",
  async (
    { excerptId, note, storyId }: { excerptId: string; note: string; storyId?: string },
    { getState, dispatch }
  ) => {
    const r = await qceService.updateExcerptNote(tokenOf(getState), excerptId, note);
    if (storyId) dispatch(fetchStoryCodes(storyId));
    return r;
  }
);

export const addExcerptTheme = createAsyncThunk(
  "qce/addExcerptTheme",
  async ({ excerptId, themeId }: { excerptId: string; themeId: string }, { getState, dispatch }) => {
    const r = await qceService.addExcerptTheme(tokenOf(getState), excerptId, themeId);
    refetchLoadedScopes(getState, dispatch);
    return r;
  }
);

// ----- Auto-detection config -----
export const fetchThemeConfig = createAsyncThunk("qce/fetchThemeConfig", (projectId: string, { getState }) =>
  qceService.getThemeConfig(tokenOf(getState), projectId)
);

export const saveThemeConfig = createAsyncThunk(
  "qce/saveThemeConfig",
  ({ projectId, autoDetect }: { projectId: string; autoDetect: AutoDetectConfig }, { getState }) =>
    qceService.putThemeConfig(tokenOf(getState), projectId, autoDetect)
);

// Run-completion toasts stay up longer than the default 3s — the user may be elsewhere.
const NOTICE_MS = 6000;
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

// The app-wide toast when a run finishes ("in K recordings" only for a scoped run).
function autoDetectNotice(r: AutoDetectRunResult, isScoped: boolean) {
  const searched = r.scope?.storyIds;
  if (isScoped && searched === 0)
    return notifyInfo("Auto-detect: none of these recordings could be searched", NOTICE_MS);
  const where = isScoped && typeof searched === "number" ? ` in ${plural(searched, "recording")}` : "";
  if (r.added === 0) return notifyInfo(`Auto-detect: nothing new found${where}`, NOTICE_MS);
  const themeCount = r.themes.filter((t) => t.added > 0).length;
  return notifySuccess(
    `Auto-detect added ${plural(r.added, "excerpt")} to ${plural(themeCount, "theme")}${where}`,
    NOTICE_MS
  );
}

// Run detection on existing recordings. Owned here (not by the dialog), so the
// dialog can close mid-run: the request is never aborted, the result arrives as a
// toast, and theme counts + any open theme detail's excerpts refresh regardless.
// One run per project at a time (`condition`).
export const runThemeDetection = createAsyncThunk<
  AutoDetectRunResult,
  { projectId: string; body: AutoDetectRunRequest },
  { state: RootState; rejectValue: string }
>(
  "qce/runThemeDetection",
  async ({ projectId, body }, { getState, dispatch, rejectWithValue }) => {
    try {
      const r = await qceService.runThemeConfig(tokenOf(getState), projectId, body);
      refetchLoadedScopes(getState, dispatch);
      const loadedCodes = getState().qce.codesStatus;
      body.themeIds.filter((id) => loadedCodes[id]).forEach((id) => dispatch(fetchThemeCodes(id)));
      if (r.added > 0) dispatch(refetchCodesForAiNotes(body.themeIds));
      dispatch(autoDetectNotice(r, Boolean(body.storyIds?.length)));
      return r;
    } catch (e: any) {
      const message = qceErrMsg(e, "Auto-detect couldn't search this project's recordings");
      dispatch(notifyError(message, NOTICE_MS));
      return rejectWithValue(message);
    }
  },
  { condition: ({ projectId }, { getState }) => getState().qce.autoDetectRuns[projectId]?.status !== "running" }
);

// KS caps skipNames at 50.
const MAX_SKIP_NAMES = 50;

// Generate theme suggestions (generate_themes) into the browser-kept list for this
// scope. Like auto-detect, owned here so the dialog can close mid-run; new results
// are appended (deduped by name) and announced by toast.
export const runGenerateThemes = createAsyncThunk<
  number,
  {
    orgId: string;
    projectId: string;
    storyIds?: string[];
    storageKey: string;
    skipNames: string[];
    scopeLabel?: string;
  },
  { state: RootState; rejectValue: string }
>(
  "qce/runGenerateThemes",
  async ({ orgId, projectId, storyIds, storageKey, skipNames, scopeLabel }, { dispatch, rejectWithValue }) => {
    try {
      const found = await agentService.generateThemes(orgId, projectId, {
        ...(storyIds?.length ? { storyIds } : {}),
        ...(skipNames.length ? { skipNames: skipNames.slice(0, MAX_SKIP_NAMES) } : {}),
      });
      const added = appendSuggestions(storageKey, found || []);
      const where = scopeLabel ? ` · ${scopeLabel}` : "";
      dispatch(
        added
          ? notifySuccess(`${plural(added, "new theme suggestion")} ready${where}`, NOTICE_MS)
          : notifyInfo("No new themes found — try a different set of recordings.", NOTICE_MS)
      );
      return added;
    } catch (e: any) {
      const message = e?.message || "Could not generate themes";
      dispatch(notifyError(message, NOTICE_MS));
      return rejectWithValue(message);
    }
  },
  { condition: ({ storageKey }, { getState }) => getState().qce.generateRuns[storageKey]?.status !== "running" }
);

// Merge over the cached row: the PATCH response may omit list-only joins
// (theme name/color, story meta), which must survive the swap.
function mergeCode(s: QceState, fresh?: Partial<ThemeCode> & { id: string; reanchored?: boolean }) {
  if (!fresh?.id) return;
  // Drop `reanchored` (response-only flag) and undefined fields before merging.
  const defined = Object.fromEntries(Object.entries(fresh).filter(([k, v]) => k !== "reanchored" && v !== undefined));
  const swap = (list?: ThemeCode[]) => {
    const i = list?.findIndex((c) => c.id === fresh.id) ?? -1;
    if (list && i >= 0) list[i] = { ...list[i], ...defined };
  };
  Object.values(s.codesByTheme).forEach(swap);
  Object.values(s.storyCodes).forEach(swap);
}

const qceSlice = createSlice({
  name: "qce",
  initialState,
  reducers: {
    setPendingSelection(s, a: PayloadAction<PendingSelection>) {
      s.pendingSelection = a.payload;
    },
    clearPendingSelection(s) {
      s.pendingSelection = null;
    },
    setShowHighlights(s, a: PayloadAction<boolean>) {
      s.showHighlights = a.payload;
    },
    // Forget a finished run so the dialog reopens on a fresh form.
    clearAutoDetectRun(s, a: PayloadAction<string>) {
      if (s.autoDetectRuns[a.payload]?.status !== "running") delete s.autoDetectRuns[a.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(runGenerateThemes.pending, (s, a) => {
        s.generateRuns[a.meta.arg.storageKey] = { status: "running" };
      })
      .addCase(runGenerateThemes.fulfilled, (s, a) => {
        s.generateRuns[a.meta.arg.storageKey] = { status: "done", added: a.payload };
      })
      .addCase(runGenerateThemes.rejected, (s, a) => {
        if (a.meta.condition) return; // skipped: already generating for this scope
        s.generateRuns[a.meta.arg.storageKey] = { status: "error", error: a.payload ?? "Could not generate themes" };
      })
      .addCase(runThemeDetection.pending, (s, a) => {
        s.autoDetectRuns[a.meta.arg.projectId] = { status: "running", themeIds: a.meta.arg.body.themeIds };
      })
      .addCase(runThemeDetection.fulfilled, (s, a) => {
        s.autoDetectRuns[a.meta.arg.projectId] = {
          status: "done",
          themeIds: a.meta.arg.body.themeIds,
          result: a.payload,
        };
      })
      .addCase(runThemeDetection.rejected, (s, a) => {
        if (a.meta.condition) return; // skipped: a run for this project is already in flight
        s.autoDetectRuns[a.meta.arg.projectId] = {
          status: "error",
          themeIds: a.meta.arg.body.themeIds,
          error: a.payload ?? "Auto-detect failed",
        };
      })
      .addCase(fetchThemes.pending, (s, a) => {
        const k = a.meta.arg ?? GLOBAL_SCOPE;
        s.themesStatusByScope[k] = "loading";
        s.themesReqIdByScope[k] = a.meta.requestId;
      })
      .addCase(fetchThemes.fulfilled, (s, a) => {
        const k = a.meta.arg ?? GLOBAL_SCOPE;
        if (a.meta.requestId !== s.themesReqIdByScope[k]) return; // superseded by a newer fetch
        s.themesStatusByScope[k] = "succeeded";
        s.themesByScope[k] = a.payload || [];
      })
      .addCase(fetchThemes.rejected, (s, a) => {
        const k = a.meta.arg ?? GLOBAL_SCOPE;
        if (a.meta.requestId !== s.themesReqIdByScope[k]) return;
        s.themesStatusByScope[k] = "failed";
      })
      .addCase(fetchThemeCodes.pending, (s, a) => {
        s.codesStatus[a.meta.arg] = "loading";
        s.codesReqId[a.meta.arg] = a.meta.requestId;
      })
      .addCase(fetchThemeCodes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.codesReqId[a.meta.arg]) return; // superseded
        s.codesStatus[a.meta.arg] = "succeeded";
        s.codesByTheme[a.meta.arg] = a.payload || [];
      })
      .addCase(fetchThemeCodes.rejected, (s, a) => {
        if (a.meta.requestId !== s.codesReqId[a.meta.arg]) return;
        s.codesStatus[a.meta.arg] = "failed";
      })
      .addCase(fetchStoryCodes.pending, (s, a) => {
        s.storyCodesReqId[a.meta.arg] = a.meta.requestId;
      })
      .addCase(fetchStoryCodes.fulfilled, (s, a) => {
        if (a.meta.requestId !== s.storyCodesReqId[a.meta.arg]) return; // superseded
        s.storyCodes[a.meta.arg] = a.payload || [];
      })
      .addCase(updateCode.fulfilled, (s, a) => mergeCode(s, a.payload))
      .addCase(reviewCode.pending, (s, a) => {
        const { codeId, review } = a.meta.arg;
        const accepted = review.review_status === "accepted";
        mergeCode(s, {
          id: codeId,
          review_status: review.review_status,
          review_remark: accepted ? review.review_remark || null : null,
          reviewed_at: accepted ? new Date().toISOString() : null,
        });
      })
      .addCase(reviewCode.fulfilled, (s, a) => mergeCode(s, a.payload))
      .addCase(reviewCode.rejected, (s, a) => {
        // null, not undefined, so mergeCode restores fields that were unset.
        const prev = a.meta.arg.previous;
        mergeCode(s, {
          id: a.meta.arg.codeId,
          review_status: prev.review_status ?? null,
          review_remark: prev.review_remark ?? null,
          reviewed_at: prev.reviewed_at ?? null,
        });
      });
  },
});

export const { setPendingSelection, clearPendingSelection, setShowHighlights, clearAutoDetectRun } = qceSlice.actions;

// ----- Typed dispatch + convenience hooks -----
type QceDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

export const useQceDispatch = () => useDispatch<QceDispatch>();

/**
 * Themes list — fetches on mount and whenever the project scope changes.
 * Pass a `projectId` for a project tab (only that project's themes); omit it for
 * the org-wide global tab.
 */
export function useThemes(projectId?: string) {
  const dispatch = useQceDispatch();
  const key = scopeKey(projectId);
  const themes = useSelector((s: RootState) => s.qce.themesByScope[key] ?? EMPTY_THEMES);
  const status = useSelector((s: RootState): Status => s.qce.themesStatusByScope[key] ?? "idle");
  useEffect(() => {
    // Fetch this scope exactly once. Each scope owns its status, so mounting a
    // second consumer with a DIFFERENT scope never flips this one back to idle —
    // no cross-scope refetch ping-pong (the Auto-detect memory-leak fix).
    if (status === "idle") dispatch(fetchThemes(projectId));
  }, [status, key, projectId, dispatch]);
  return {
    themes,
    loading: status === "idle" || status === "loading",
    error: status === "failed",
    refetch: () => dispatch(fetchThemes(projectId)),
  };
}

/** A theme's codes — refetches when the theme id changes. */
export function useThemeCodes(themeId: string) {
  const dispatch = useQceDispatch();
  const codes = useSelector((s: RootState) => s.qce.codesByTheme[themeId]);
  const status = useSelector((s: RootState) => s.qce.codesStatus[themeId]);
  useEffect(() => {
    dispatch(fetchThemeCodes(themeId));
  }, [themeId, dispatch]);
  return {
    codes: codes || EMPTY_CODES,
    loading: (status === "loading" || status === undefined) && !codes,
    error: status === "failed",
  };
}

/** A story's codes for the Hub overlay — fetches when active. */
export function useStoryCodes(storyId: string | undefined, active: boolean) {
  const dispatch = useQceDispatch();
  const codes = useSelector((s: RootState) => (storyId ? s.qce.storyCodes[storyId] : undefined));
  useEffect(() => {
    if (active && storyId) dispatch(fetchStoryCodes(storyId));
  }, [active, storyId, dispatch]);
  return active && storyId ? codes || EMPTY_CODES : EMPTY_CODES;
}

/** Hub "Show highlights" overlay toggle, shared across the transcript + coding controller. */
export function useShowHighlights(): [boolean, (next: boolean) => void] {
  const dispatch = useQceDispatch();
  const showHighlights = useSelector((s: RootState) => s.qce.showHighlights);
  return [showHighlights, (next: boolean) => dispatch(setShowHighlights(next))];
}

export default qceSlice.reducer;
